import React, { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, Link } from 'react-router-dom'
import {
  Box, useTheme,
  Button,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  CssBaseline,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  InputAdornment,
  IconButton
} from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { registerUser, clearRegisterError } from './registerActions'
import logo from '../../assets/logo.png'

const CANADIAN_PROVINCES = [
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'YT', label: 'Yukon' }
]

const Register = () => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { loading, error } = useSelector((state) => state.register)
  const [activeStep, setActiveStep] = useState(0)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  // Removed passwordError state as we're now using formErrors
  const [formErrors, setFormErrors] = useState({})
  const [touched, setTouched] = useState({})
  // Map gender display names to their corresponding IDs
  const genderMap = useMemo(() => ({
    'Male': 1,
    'Female': 2,
    'Non-Binary': 3,
    'Prefer not to say': 4
  }), [])

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password1: '',
    password2: '',
    first_name: '',
    last_name: '',
    contact_number: '',
    birthday: null,
    location: '',
    gender: '',
    user_type: 1,
    is_indigenous_people: false
  })

  useEffect(() => {
    // Clear any previous errors when component mounts
    dispatch(clearRegisterError())
  }, [dispatch])

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const handleBlur = (e) => {
    const { name } = e.target
    setTouched(prev => ({
      ...prev,
      [name]: true
    }))
  }

  const isAtLeast18 = (date) => {
    const today = new Date()
    const birthDate = new Date(date)
    const age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      return age - 1 >= 18
    }
    return age >= 18
  }

  const handleDateChange = (date) => {
    const dateString = date ? date.toISOString().split('T')[0] : null
    const newErrors = { ...formErrors }

    // Clear birthday error when date changes
    if (newErrors.birthday) {
      delete newErrors.birthday
    }

    // Check if the new date is valid (user is at least 18)
    if (date && !isAtLeast18(date)) {
      newErrors.birthday = 'You must be at least 18 years old to register'
    }

    setFormErrors(newErrors)
    setFormData(prev => ({
      ...prev,
      birthday: dateString
    }))

    // Mark the field as touched to show the error
    setTouched(prev => ({
      ...prev,
      birthday: true
    }))
  }

  const validateForm = (step = activeStep) => {
    const errors = {}

    if (step === 0) {
      if (!formData.email?.trim()) errors.email = 'Email is required'
      else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Please enter a valid email'

      if (!formData.username?.trim()) errors.username = 'Username is required'
      else if (formData.username.length < 3) errors.username = 'Username must be at least 3 characters'

      if (!formData.password1) errors.password1 = 'Password is required'
      else if (formData.password1.length < 8) errors.password1 = 'Password must be at least 8 characters'

      if (!formData.password2) errors.password2 = 'Please confirm your password'
      else if (formData.password1 !== formData.password2) {
        errors.password2 = 'Passwords do not match'
      }
    } else if (step === 1) {
      if (!formData.first_name?.trim()) errors.first_name = 'First name is required'
      if (!formData.last_name?.trim()) errors.last_name = 'Last name is required'

      // Birthday validation
      if (!formData.birthday) {
        errors.birthday = 'Birthday is required'
      } else {
        const birthDate = new Date(formData.birthday)
        if (!isAtLeast18(birthDate)) {
          errors.birthday = 'You must be at least 18 years old to register'
        }
      }

      if (!formData.gender) errors.gender = 'Gender is required'
      if (!formData.location) errors.location = 'Location is required'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Removed validatePasswords as it's now handled by validateForm

  const handleNext = () => {
    // Mark all fields as touched to show errors
    setTouched({
      email: true,
      username: true,
      password1: true,
      password2: true
    })

    if (!validateForm()) {
      return
    }
    setActiveStep((prevStep) => prevStep + 1)
  }

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1)
  }

  // Handle form submission with better mobile UX
  const handleSubmit = async (e) => {
    e.preventDefault()

    // Mark all personal info fields as touched
    setTouched(prev => ({
      ...prev,
      first_name: true,
      last_name: true,
      birthday: true,
      gender: true,
      location: true
    }))

    // Ensure gender is set to the ID from the map
    const submitData = {
      ...formData,
      gender: genderMap[formData.gender] || formData.gender
    }

    // Validate final step
    if (!validateForm(1)) {
      // Scroll to first error
      const firstError = Object.keys(formErrors)[0]
      if (firstError) {
        const element = document.querySelector(`[name="${firstError}"]`)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' })
          element.focus({ preventScroll: true })
        }
      }
      return
    }

    try {
      await dispatch(registerUser(submitData))
      // Always go to success page, which will handle the redirect
      navigate('/register/done')
    } catch (error) {
      console.error('Registration error:', error)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: 'background.default',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      overflow: 'hidden',
      pt: 'env(safe-area-inset-top, 0px)',
      pb: 'env(safe-area-inset-bottom, 0px)',
    }}>
      <CssBaseline />
      {/* Decorative gradient blobs */}
      <Box sx={{
        position: 'absolute',
        top: -80,
        right: -80,
        width: 280,
        height: 280,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,56,92,0.12) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />
      <Box sx={{
        position: 'absolute',
        bottom: -60,
        left: -60,
        width: 220,
        height: 220,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(0,201,177,0.09) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

    <Box sx={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      py: 4,
      px: 3,
      maxWidth: 400,
      width: '100%',
      mx: 'auto',
      position: 'relative',
      zIndex: 1,
    }}>
      <Box
        component="img"
        src={logo}
        alt="SwipeeIT"
        sx={{
          width: 140,
          height: 'auto',
          mb: 4,
          mx: 'auto',
          display: 'block',
          objectFit: 'contain',
          filter: theme.palette.mode === 'dark' ? 'invert(1) brightness(0) invert(1)' : 'none',
        }}
      />
      <Typography
        component="h1"
        variant="h5"
        sx={{ mb: 0.5, fontWeight: 700 }}
      >
        Create an Account
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Join SwipeeIT to find your perfect home
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{ width: '100%' }}
      >
        <Stepper activeStep={activeStep} orientation="vertical" sx={{ mb: 4 }}>
          {/* Authentication Step */}
          <Step key="authentication">
            <StepLabel>Authentication</StepLabel>
            <StepContent>
              <Box sx={{ mt: 2, '& > *:not(:last-child)': { mb: 3 } }}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!(touched.email && formErrors.email)}
                  helperText={touched.email && formErrors.email}
                  required
                  autoComplete="email"
                  margin="normal"
                />

                <TextField
                  fullWidth
                  label="Username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!(touched.username && formErrors.username)}
                  helperText={touched.username && formErrors.username}
                  required
                  margin="normal"
                />

                <TextField
                  fullWidth
                  label="Password"
                  name="password1"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password1}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  autoComplete="new-password"
                  margin="normal"
                  error={!!(touched.password1 && formErrors.password1)}
                  helperText={touched.password1 && formErrors.password1}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                <TextField
                  fullWidth
                  label="Confirm Password"
                  name="password2"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.password2}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  autoComplete="new-password"
                  margin="normal"
                  error={!!(touched.password2 && formErrors.password2)}
                  helperText={touched.password2 && formErrors.password2}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                <Button
                  variant="contained"
                  onClick={handleNext}
                  fullWidth
                  sx={{
                    mt: 2,
                    py: 1.5,
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #FF385C 0%, #FF6B83 100%)',
                    boxShadow: '0 4px 16px rgba(255,56,92,0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #D4003A 0%, #FF385C 100%)',
                      boxShadow: '0 6px 20px rgba(255,56,92,0.42)',
                    },
                  }}
                >
                  Continue
                </Button>
              </Box>
            </StepContent>
          </Step>

          {/* Personal Info Step */}
          <Step key="personal-info">
            <StepLabel>Personal Information</StepLabel>
            <StepContent>
              <Box sx={{ mt: 2, '& > *:not(:last-child)': { mb: 3 } }}>
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  <FormControl fullWidth>
                    <TextField
                      label="First Name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      required
                      autoComplete="given-name"
                      error={!!(touched.first_name && formErrors.first_name)}
                      helperText={touched.first_name && formErrors.first_name}
                      onBlur={handleBlur}
                    />
                  </FormControl>
                  <FormControl fullWidth>
                    <TextField
                      label="Last Name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      required
                      autoComplete="family-name"
                      error={!!(touched.last_name && formErrors.last_name)}
                      helperText={touched.last_name && formErrors.last_name}
                      onBlur={handleBlur}
                    />
                  </FormControl>
                </Box>

                <FormControl fullWidth error={!!(touched.birthday && formErrors.birthday)} margin="normal" required>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Birthday"
                      value={formData.birthday ? new Date(formData.birthday) : null}
                      onChange={handleDateChange}
                      inputFormat="yyyy-MM-dd"
                      maxDate={new Date(new Date().setFullYear(new Date().getFullYear() - 18))}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="birthday"
                          onBlur={handleBlur}
                          error={!!(touched.birthday && formErrors.birthday)}
                          helperText={(touched.birthday && formErrors.birthday) || 'You must be at least 18 years old to register'}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </LocalizationProvider>
                  {touched.birthday && formErrors.birthday && (
                    <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5, display: 'block' }}>
                      {formErrors.birthday}
                    </Typography>
                  )}
                </FormControl>

                <FormControl fullWidth margin="normal" error={!!(touched.gender && formErrors.gender)} required>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label="Gender"
                  >
                    <MenuItem value=""><em>Select a gender</em></MenuItem>
                    <MenuItem value="1">Male</MenuItem>
                    <MenuItem value="2">Female</MenuItem>
                    <MenuItem value="3">Non-Binary</MenuItem>
                    <MenuItem value="4">Prefer not to say</MenuItem>
                  </Select>
                  {touched.gender && formErrors.gender && (
                    <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5, display: 'block' }}>
                      {formErrors.gender}
                    </Typography>
                  )}
                </FormControl>

                <FormControl fullWidth margin="normal" error={!!(touched.location && formErrors.location)} required>
                  <InputLabel>Location</InputLabel>
                  <Select
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label="Location"
                  >
                    <MenuItem value=""><em>Select a province/territory</em></MenuItem>
                    {CANADIAN_PROVINCES.map((province) => (
                      <MenuItem key={province.value} value={province.value}>
                        {province.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.location && formErrors.location && (
                    <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5, display: 'block' }}>
                      {formErrors.location}
                    </Typography>
                  )}
                </FormControl>

                <TextField
                  fullWidth
                  label="Contact Number (Optional)"
                  name="contact_number"
                  value={formData.contact_number}
                  onChange={handleChange}
                  type="tel"
                  margin="normal"
                  inputProps={{
                    inputMode: 'tel',
                    pattern: '[0-9]*'
                  }}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      name="is_indigenous_people"
                      checked={formData.is_indigenous_people}
                      onChange={handleChange}
                      color="primary"
                    />
                  }
                  label="I identify as Indigenous People"
                  sx={{ display: 'block', mt: 1 }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, gap: 2 }}>
                  <Button
                    onClick={handleBack}
                    variant="outlined"
                    sx={{ flex: 1 }}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    sx={{
                      flex: 1,
                      py: 1.5,
                      fontWeight: 700,
                      background: 'linear-gradient(135deg, #FF385C 0%, #FF6B83 100%)',
                      boxShadow: '0 4px 16px rgba(255,56,92,0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #D4003A 0%, #FF385C 100%)',
                        boxShadow: '0 6px 20px rgba(255,56,92,0.42)',
                      },
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign Up'}
                  </Button>
                </Box>
              </Box>
            </StepContent>
          </Step>
        </Stepper>

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" color="text.secondary">
            Already have an account?{' '}
            <Link
              to="/login"
              style={{ textDecoration: 'none', color: theme.palette.primary.main, fontWeight: 600 }}
            >
              Sign In
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  </Box>
  )
}

export default Register
